import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ChartContainer } from "../ui/chart";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from "recharts";
import {
  Wifi,
  WifiOff,
  Activity,
  Clock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useZonePagination } from "../../../hooks/basic-reports/use-meter-pagination";
import MeterPagination from "./MeterPagination";
import ExportButton from "./ExportButton";
import { CHIP_COLORS } from "../utils/colors";
import { useReportData } from "../../../contexts/ReportDataContext";

const chartConfig = {
  connected: { label: "Connected", color: CHIP_COLORS.success },
  disconnected: { label: "Disconnected", color: CHIP_COLORS.failed },
  communication: { label: "Comm. %", color: CHIP_COLORS.info },
};

const ITEMS_PER_PAGE = 10;

const Operations = () => {
  const { reports, loading, error, progress } = useReportData();
  const [showMeterTable, setShowMeterTable] = useState(true);
  const { getPage, setPage } = useZonePagination();

  if (loading)
    return (
      <div className="p-10 text-center">
        <div className="animate-pulse text-muted-foreground">
          {progress || "Loading Report Data..."}
        </div>
      </div>
    );
  if (error)
    return <div className="p-10 text-center text-destructive">{error}</div>;
  if (!reports)
    return <div className="p-10 text-center">No data available</div>;

  const { operations } = reports;
  const { currentStatus } = operations;
  const totalMeters = currentStatus.total;

  // Connectivity trend chart
  const connectivityTrend = operations.hourlyConnectivity.map((d) => ({
    time: d.time,
    connected: d.connected,
    disconnected: d.disconnected,
    label: d.time,
  }));

  // Communication stats
  const communicationStats = operations.communicationStats.map((d) => ({
    time: d.hour,
    successRate: d.value,
    label: d.hour,
  }));

  // Per-meter operations data
  const perMeterData = operations.perMeter;
  const events = operations.events;

  // Pagination
  const currentPage = getPage("meters");
  const totalPages = Math.ceil(perMeterData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, perMeterData.length);
  const paginatedMeters = perMeterData.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success/10">
                <Wifi className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  Connected Meters
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-lg font-bold">
                    {currentStatus.connected.toLocaleString()}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    / {totalMeters.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-destructive/10">
                <WifiOff className="w-4 h-4 text-destructive" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Disconnected</p>
                <p className="text-lg font-bold">
                  {currentStatus.disconnected.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-warning/10">
                <Activity className="w-4 h-4 text-warning" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Relay Changes</p>
                <p className="text-lg font-bold">
                  {currentStatus.relayChanges.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info/10">
                <Clock className="w-4 h-4 text-info" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Uptime</p>
                <p className="text-lg font-bold">
                  {currentStatus.uptimePercent}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-8">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold">
                  Connectivity Trend
                </CardTitle>
                <CardDescription>
                  Active vs Inactive meters over time
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-xs">
                Real-time
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={connectivityTrend}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.5}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={30}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="font-medium text-xs mb-2">
                              {payload[0].payload.label}
                            </p>
                            <div className="space-y-1">
                              {payload.map((entry: any) => (
                                <div
                                  key={entry.name}
                                  className="flex items-center gap-2 text-xs"
                                >
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-muted-foreground capitalize">
                                    {entry.name}:
                                  </span>
                                  <span className="font-mono font-medium">
                                    {entry.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="connected"
                    stackId="a"
                    fill={CHIP_COLORS.success}
                    radius={[0, 0, 4, 4]}
                  />
                  <Bar
                    dataKey="disconnected"
                    stackId="a"
                    fill={CHIP_COLORS.failed}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card className="col-span-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Communication Success
            </CardTitle>
            <CardDescription>% of successful readings</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={communicationStats}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    opacity={0.5}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    hide
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-background border rounded-lg shadow-lg p-3">
                            <p className="text-sm font-medium">
                              {Number(payload[0].value).toFixed(1)}% Success
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="successRate"
                    stroke={CHIP_COLORS.info}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Events Log Table */}
      {events.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Recent Meter Events
            </CardTitle>
            <CardDescription>
              Log of critical meter events ({events.length} events)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[300px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Meter ID</TableHead>
                    <TableHead>Event Code</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.slice(0, 50).map((event, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {event.timestamp}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {event.meterId.replace(/^Demo1_/, "")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{event.eventCode}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {event.eventDescription}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Meter Operations Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold">
                Per-Meter Operations
              </CardTitle>
              <CardDescription>
                Detailed operational status for {perMeterData.length} meters
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton reportName="Meter Operations Summary" />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-primary"
                onClick={() => setShowMeterTable(!showMeterTable)}
              >
                {showMeterTable ? "Hide Details" : "Show Details"}
              </Button>
            </div>
          </div>
        </CardHeader>
        {showMeterTable && (
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter ID</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Connected Reads</TableHead>
                  <TableHead className="text-right">
                    Disconnected Reads
                  </TableHead>
                  <TableHead className="text-right">Relay Changes</TableHead>
                  <TableHead className="text-right">Uptime %</TableHead>
                  <TableHead className="text-right">Power On (Hours)</TableHead>
                  <TableHead className="text-right">Last Reading</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMeters.map((meter) => (
                  <TableRow key={meter.meterId} className="hover:bg-muted/50">
                    <TableCell className="font-mono text-sm whitespace-nowrap">
                      {meter.meterId.replace(/^Demo1_/, "")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={
                          meter.status === "Online"
                            ? "bg-success/10 text-success border-success/20"
                            : "bg-destructive/10 text-destructive border-destructive/20"
                        }
                      >
                        {meter.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.connectedReadings}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.disconnectedReadings}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.relayChanges}
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.uptimePercent}%
                    </TableCell>
                    <TableCell className="text-right">
                      {meter.totalPowerOnHours}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {meter.lastReading}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <MeterPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={perMeterData.length}
              startIndex={startIndex}
              endIndex={endIndex}
              onPageChange={(page) => setPage("meters", page)}
              hasNextPage={currentPage < totalPages}
              hasPrevPage={currentPage > 1}
            />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default Operations;
